import "../store/store.scss";

import React from "react";
import { Redirect, withRouter } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  UncontrolledTooltip,
} from "reactstrap";

import getAlertChannels from "../../services/alertChannelService";
import getButtonsTypes from "../../services/buttonsTypesService";
import getGesturePresets from "../../services/gesturePresetService";
import { getStoreHostnames, restartWithToast } from "../../services/machineOperationsService";
import getSlackChannels from "../../services/slackChannelService";
import getStoreManagers from "../../services/storeManagerService";
import { toggleStoreKeyAccount } from "../../services/keyAccountPresettingsService";
import { deleteStore, saveStore } from "../../services/storeService";
import getUploadBuckets from "../../services/uploadBucketService";
import getSensitivities from "../../services/sensitivityService";
import DisplayWhen from "../common/base/DisplayWhen";
import BaseForm from "../common/BaseForm";
import ToggleSwitch from "../common/controls/ToggleSwitch/ToggleSwitch";
import ReadOnlyField from "../common/ReadOnlyField";
import Store from "../models/StoreModel";
import { editorRole, qualityRole } from "../shared/constants";
import FormDataContext from "../shared/contexts/FormDataContext";
import userHasRole from "../shared/utils/authUtils";
import FieldBuilder from "../shared/utils/FieldBuilder";
import ResponsiveRow from "./base/ResponsiveRow";
import AlertChannelForm from "./store/AlertChannelForm";
import KeyAccountSection from "./store/KeyAccountSection";
import STORE_DEFAULTS from "../../config/storeDefaults";

const showOldLabels = false;
const showNewLabels = true;

class StoreConfigForm extends BaseForm {
  store = new Store();
  state = {
    data: {},
    isRestarting: false,
    sensitivities: [],
    toggleConfigsOldLabel: [
      { name: "no_suspicious", value: "Slightly suspicious", inverted: true, datagroup: "suspiciousDatagroup" },
      { name: "no_stroller", value: "Stroller", inverted: true, datagroup: "productIntoStrollerDatagroup" },
      { name: "send_store_normal_bag", value: "Store normal bag", datagroup: "storeNormalBagDatagroup" },
      { name: "send_gesture_into_body", value: "Gesture into body", datagroup: "gestureIntoBodyDatagroup" },
      { name: "send_client_normal_bag", value: "Client normal bag", datagroup: "clientNormalBagDatagroup" },
      { name: "no_suspicious_bag", value: "Suspicious bag", inverted: true, datagroup: "suspiciousBagDatagroup" },
    ],
    toggleConfigBodyParent: {
      name: "bodyToggleGroup",
      value: "Body labels",
      parentgroup: "bodyToggleGroup",
    },
    toggleConfigsNewLabelBody: [
      // Always active : shown but disabled
      {
        name: "body_theft",
        value: "Body theft",
        disabled: true,
        childgroup: "bodyToggleGroup",
        tooltip: "Always active",
      },
      {
        name: "gesture_into_body",
        value: "Gesture into body",
        datagroup: "gestureIntoBodyDatagroup",
        childgroup: "bodyToggleGroup",
      },
    ],
    toggleConfigBagParent: { name: "bagToggleGroup", value: "Bag labels", parentgroup: "bagToggleGroup" },
    toggleConfigsNewLabelBag: [
      {
        name: "product_in_personal_bag",
        value: "Product in personal bag",
        childgroup: "bagToggleGroup",
      },
      { name: "product_into_backpack", value: "Product into backpack", childgroup: "bagToggleGroup" },
      {
        name: "suspicious_bag",
        value: "Suspicious bag",
        datagroup: "suspiciousBagDatagroup",
        childgroup: "bagToggleGroup",
      },
      {
        name: "product_into_stroller",
        value: "Product into stroller",
        datagroup: "productIntoStrollerDatagroup",
        childgroup: "bagToggleGroup",
      },
      {
        name: "client_normal_bag",
        value: "Client normal bag",
        datagroup: "clientNormalBagDatagroup",
        childgroup: "bagToggleGroup",
      },
      {
        name: "store_normal_bag",
        value: "Store normal bag",
        datagroup: "storeNormalBagDatagroup",
        childgroup: "bagToggleGroup",
      },
    ],
    toggleConfigOthersusParent: {
      name: "othersusToggleGroup",
      value: "Other suspicious labels",
      datagroup: "suspiciousDatagroup",
      parentgroup: "othersusToggleGroup",
    },
    toggleConfigsNewLabelOtherSus: [
      { name: "deblistering", value: "Deblistering", childgroup: "othersusToggleGroup" },
      { name: "consumption", value: "Consumption", childgroup: "othersusToggleGroup" },
      { name: "burst_shot", value: "Burst shot", childgroup: "othersusToggleGroup" },
      { name: "other_suspicious", value: "Other", childgroup: "othersusToggleGroup" },
    ],
    toggleConfigsDisplay: [
      { name: "show_cam_number", value: "Show cam number" },
      { name: "show_latency", value: "Show latency" },
      { name: "show_alert_time", value: "Show alert time" },
      { name: "show_alert_type", value: "Show alert type" },
    ],
    // Relevance Check Gesture configuration
    gestureConfigs: [
      { key: "gesture-into-body", label: "Gesture into body" },
      { key: "suspicious-bag", label: "Suspicious bag" },
      { key: "product-into-stroller", label: "Product into stroller" },
      { key: "client-normal-bag", label: "Client normal bag" },
      { key: "store-normal-bag", label: "Store normal bag" },
      { key: "deblistering", label: "Deblistering" },
      { key: "consumption", label: "Consumption" },
      { key: "burst-shot", label: "Burst shot" },
      { key: "fall", label: "Fall" },
      { key: "checkout", label: "Checkout" },
      { key: "other-suspicious", label: "Other suspicious" },
      { key: "theft", label: "Concealment" },
      { key: "backpack", label: "Backpack" },
    ],
    alertChannels: [],
    gesturePresets: {},
    slackChannels: [],
    storeManagers: [],
    errors: {},
    buttonsTypes: [],
    uploadBuckets: [],
    redirectStore: null,
    showLocalInferenceModal: false,
    isSaving: false,
  };
  schema = this.store.getSchema();

  setValueAndNotify = (name, value) => {
    this.setValue(name, value);
    if (name === "in_simulation" && this.props.onSimulationChange) {
      this.props.onSimulationChange(value);
    }
  };

  async fetchButtonsTypes() {
    // eslint-disable-next-line camelcase
    const { data: buttons_types } = await getButtonsTypes();
    // eslint-disable-next-line camelcase
    return buttons_types;
  }

  async fetchAlertChannels() {
    let { data: alertChannels } = await getAlertChannels();
    alertChannels = alertChannels.map((alertChannel) => {
      if (
        !(
          alertChannel.name === "RabbitMQ - label - DNS" ||
          alertChannel.name === "simulation" ||
          alertChannel.name === "label + simulation"
        )
      ) {
        return { ...alertChannel, enabled: false };
      }
      return alertChannel;
    });
    alertChannels.unshift({ id: 0, name: "Not allocated" });
    return alertChannels;
  }

  async fetchSlackChannels() {
    const { data: slackChannels } = await getSlackChannels();
    slackChannels.unshift({ id: "0", name: "Not allocated" });
    return slackChannels;
  }

  async fetchSensitivities() {
    const { data } = await getSensitivities();
    const flat = [...(data?.body || []), ...(data?.bag || [])];
    const sensitivities = flat
      .map((s) => ({
        id: s.id,
        name: `${s.sensitivity_type === "body" ? "Body" : "Bag"} Level ${s.level}`,
        type: s.sensitivity_type,
        level: s.level,
        is_default: !!s.is_default,
      }))
      .sort((a, b) => a.id - b.id);
    return sensitivities;
  }

  async fetchGesturePresets() {
    const { data: gesturePresets } = await getGesturePresets();
    return gesturePresets;
  }

  async fetchStoreManager() {
    const { data: storeManagers } = await getStoreManagers();
    return storeManagers;
  }

  async fetchUploadBuckets() {
    const { data: uploadBuckets } = await getUploadBuckets();
    uploadBuckets.unshift({ id: "0", name: "Not allocated" });
    return uploadBuckets;
  }

  /**
   * Fetches all data needed by this component.
   * All requests are parallelized.
   *
   * @returns {Promise<void>}
   */
  fetchAll = async () => {
    const [alertChannels, gesturePresets, slackChannels, storeManagers, buttonsTypes, uploadBuckets, sensitivities] =
      await Promise.all([
        this.fetchAlertChannels(),
        this.fetchGesturePresets(),
        this.fetchSlackChannels(),
        this.fetchStoreManager(),
        this.fetchButtonsTypes(),
        this.fetchUploadBuckets(),
        this.fetchSensitivities(),
      ]);

    this.setState({
      alertChannels,
      gesturePresets,
      slackChannels,
      storeManagers,
      buttonsTypes,
      uploadBuckets,
      sensitivities,
    });
  };

  isCheckboxChecked(data, checkbox) {
    const checkboxValue = data[checkbox.name];
    return checkbox.getAttribute("inverted") ? !checkboxValue : checkboxValue;
  }

  handleDatagroupToggled(data, checkbox, datagroup, checked) {
    let newData = data;
    const sameDataGroupCheckboxes = this.getCheckboxesByGroup("datagroup", datagroup);
    sameDataGroupCheckboxes.forEach((sameDataGroupCheckbox) => {
      if (sameDataGroupCheckbox.name === checkbox.name) {
        return;
      }
      if (this.isCheckboxChecked(newData, sameDataGroupCheckbox) === this.isCheckboxChecked(newData, checkbox)) {
        return;
      }
      newData = this.toggleCheckbox(newData, sameDataGroupCheckbox, checked);
      // if a checkbox from the same datagroup has a parent, handle the parent toggling
      const sameDataGroupCheckboxChildGroup = sameDataGroupCheckbox.getAttribute("childgroup");
      if (sameDataGroupCheckboxChildGroup) {
        newData = this.handleChildToggled(newData, sameDataGroupCheckbox, sameDataGroupCheckboxChildGroup);
      }
      // if a checkbox from the same datagroup is a parent, handle the children toggling
      const sameDataGroupCheckboxParentgroup = sameDataGroupCheckbox.getAttribute("parentgroup");
      if (sameDataGroupCheckboxParentgroup) {
        newData = this.handleParentToggled(newData, sameDataGroupCheckbox, sameDataGroupCheckboxParentgroup);
      }
    });
    return newData;
  }

  handleParentToggled(data, checkbox, parentgroup) {
    let newData = data;
    const childGroupCheckboxes = this.getCheckboxesByGroup("childgroup", parentgroup);

    childGroupCheckboxes.forEach((childCheckbox) => {
      if (childCheckbox.disabled) {
        return;
      }
      newData = this.toggleCheckbox(newData, childCheckbox, this.isCheckboxChecked(newData, checkbox));
      const childDataGroup = childCheckbox.getAttribute("datagroup");
      // if the child checkbox belongs to a datagroup, all checkboxes with same datagroup should change
      if (childDataGroup) {
        newData = this.toggleCheckboxesByGroup(
          newData,
          "datagroup",
          childDataGroup,
          this.isCheckboxChecked(newData, checkbox),
          childCheckbox
        );
      }
    });
    return newData;
  }

  handleChildToggled(data, childCheckbox, childgroup) {
    let newData = data;
    const parentGroupName = childgroup;
    const parentCheckbox = this.getCheckboxesByGroup("parentgroup", parentGroupName)[0];
    if (!parentGroupName) {
      return newData;
    }
    if (!parentCheckbox) {
      return newData;
    }
    let checked = "undefined";
    if (this.isCheckboxChecked(newData, childCheckbox)) {
      // When checking a child toggle, check the parent toggle
      checked = true;
      newData = this.toggleCheckbox(newData, parentCheckbox, checked);
    } else {
      // When unchecking a child toggle, if all child toggles are unchecked, uncheck the parent too
      const childGroupCheckboxes = document.querySelectorAll(
        `[childgroup="${childCheckbox.getAttribute("childgroup")}"]`
      );
      let allUnchecked = true;
      childGroupCheckboxes.forEach((childGroupCheckbox) => {
        if (this.isCheckboxChecked(newData, childGroupCheckbox)) {
          allUnchecked = false;
        }
      });
      if (allUnchecked) {
        checked = false;
        newData = this.toggleCheckbox(newData, parentCheckbox, checked);
      }
    }
    // if parent of child belongs to a datagroup, handle datagroup toggling
    const parentDatagroup = parentCheckbox.getAttribute("datagroup");
    if (parentDatagroup && typeof checked !== "undefined") {
      newData = this.handleDatagroupToggled(newData, parentCheckbox, parentDatagroup, checked);
    }
    return newData;
  }

  handleCheckboxToggled(data, checkbox, initial) {
    if (this.state.toggleConfigsOldLabel.find((config) => config.name === checkbox.name)) {
      return data;
    }

    let newData = data;
    const datagroup = checkbox.getAttribute("datagroup");
    const parentgroup = checkbox.getAttribute("parentgroup");
    const childgroup = checkbox.getAttribute("childgroup");

    // if the checkbox belongs to a datagroup, all checkboxes with same datagroup should change
    if (datagroup) {
      newData = this.handleDatagroupToggled(newData, checkbox, datagroup);
    }

    // if the checkbox is a parentgroup, all the childgroup checkboxes should change
    if (parentgroup && !initial) {
      newData = this.handleParentToggled(newData, checkbox, parentgroup);
    }

    // if a checkbox belongs to a childgroup, handle the parent toggling
    // if it belongs to a datagroup too, its datagroup should have been already handled above
    if (childgroup) {
      newData = this.handleChildToggled(newData, checkbox, childgroup);
    }
    return newData;
  }

  toggleCheckboxesByGroup(data, groupType, groupName, checked, excludedCheckbox) {
    let newData = data;
    const groupCheckboxes = this.getCheckboxesByGroup(groupType, groupName);
    groupCheckboxes.forEach((groupCheckbox) => {
      if (this.isCheckboxChecked(newData, groupCheckbox) === checked) {
        return;
      }
      if (excludedCheckbox && groupCheckbox.name === excludedCheckbox.name) {
        return;
      }
      newData = this.toggleCheckbox(newData, groupCheckbox, checked);
    });
    return newData;
  }

  toggleCheckbox(data, checkbox, checked) {
    let newValue = !data[checkbox.name];
    if (typeof checked !== "undefined") {
      newValue = checkbox.getAttribute("inverted") ? !checked : checked;
    }
    data[checkbox.name] = newValue;
    return data;
  }

  getCheckboxesByGroup(groupType, groupName) {
    return document.querySelectorAll(`[${groupType}="${groupName}"]`);
  }

  handleCheckboxChange = (event) => {
    const checkbox = event.currentTarget;
    let newData = this.state.data;
    newData = this.handleCheckboxToggled(newData, checkbox);
    this.setState({ data: newData });
  };

  setCheckboxesEventListener() {
    const checkboxes = document.querySelectorAll("input[type=checkbox]");
    this.checkboxListenerNodes = checkboxes;
    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", this.handleCheckboxChange);
    });
  }

  removeCheckboxesEventListener() {
    if (!this.checkboxListenerNodes) return;
    this.checkboxListenerNodes.forEach((checkbox) => {
      checkbox.removeEventListener("change", this.handleCheckboxChange);
    });
    this.checkboxListenerNodes = null;
  }

  setCheckboxesInitialValues(data, initial) {
    let newData = data;
    const checkboxes = document.querySelectorAll("input[type=checkbox]");
    checkboxes.forEach((checkbox) => {
      if (this.isCheckboxChecked(newData, checkbox)) {
        newData = this.handleCheckboxToggled(newData, checkbox, initial);
      }
    });
    return newData;
  }

  updateParentGroupsFromData = (data) => {
    const { toggleConfigsNewLabelBody, toggleConfigsNewLabelBag, toggleConfigsNewLabelOtherSus } = this.state;
    const newData = { ...data };
    const allChildConfigs = [
      ...toggleConfigsNewLabelBody,
      ...toggleConfigsNewLabelBag,
      ...toggleConfigsNewLabelOtherSus,
    ];

    // Group gestures by their parent group
    const parentGroups = {};
    allChildConfigs.forEach((config) => {
      if (config.childgroup) {
        if (!parentGroups[config.childgroup]) {
          parentGroups[config.childgroup] = [];
        }
        parentGroups[config.childgroup].push(config.name);
      }
    });

    // Set parent toggle to true if any child is enabled
    Object.keys(parentGroups).forEach((parentGroup) => {
      const childGestures = parentGroups[parentGroup];
      newData[parentGroup] = childGestures.some((gesture) => newData[gesture] === true);
    });

    return newData;
  };

  async componentDidMount() {
    await this.fetchAll();
    const { store, newStore } = this.props;

    let data;
    if (store) {
      if (newStore) {
        data = this.store.getObject(store);
      } else {
        data = store;
      }
    } else {
      data = this.store.getDefaultData();
    }

    this.initialIsKeyAccount = Boolean(data.is_key_account);

    if (!Object.prototype.hasOwnProperty.call(data, "activation_date")) {
      // Ensure activation_date is always present so the onboarding section is rendered
      data.activation_date = null;
    }
    if (!Object.prototype.hasOwnProperty.call(data, "in_onboarding_remaining_days")) {
      // Ensure in_onboarding_remaining_days is always present so the onboarding section can use it
      data.in_onboarding_remaining_days = null;
    }
    if (!Object.prototype.hasOwnProperty.call(data, "in_onboarding_status")) {
      data.in_onboarding_status = null;
    }
    if (!Object.prototype.hasOwnProperty.call(data, "category")) {
      data.category = null;
    }
    if (!Object.prototype.hasOwnProperty.call(data, "zendesk_id")) {
      data.zendesk_id = null;
    }
    data.enable_bbox_client_overlay = data.enable_bbox_client_overlay ?? null;

    data.bodyToggleGroup = true;
    data.bagToggleGroup = true;
    data.othersusToggleGroup = false;

    // Initialize relevance check gesture with default values if not present
    if (!data.client_relevance_check_gestures) {
      data.client_relevance_check_gestures = {
        "gesture-into-body": false,
        "suspicious-bag": true,
        "product-into-stroller": false,
        "client-normal-bag": false,
        "store-normal-bag": false,
        deblistering: false,
        consumption: false,
        "burst-shot": false,
        fall: false,
        checkout: false,
        "other-suspicious": true,
        theft: false,
        backpack: false,
      };
    }

    if (data.id === null) {
      const onboardingButton = this.state.buttonsTypes.find((button) => button.name === "onboarding-buttons");
      if (onboardingButton) {
        data.buttons_type = onboardingButton.id;
      }
    }

    const redirectStore = null;
    this.setState({ data, redirectStore }, () => {
      const errors = this.validate();
      this.setCheckboxesEventListener();
      let newData = data;
      newData = this.setCheckboxesInitialValues(newData, true);
      // Set upload_bucket based on local_inference value AFTER state is set
      newData = this.setUploadBucketBasedOnInference(newData);
      // Preselect default sensitivities on new store using backend defaults (is_default)
      if (newStore) {
        const { sensitivities } = this.state;
        const defaultBody =
          (sensitivities || []).find((s) => s.type === "body" && s.is_default) ||
          (sensitivities || []).find((s) => s.type === "body" && s.level === 1);
        const defaultBag =
          (sensitivities || []).find((s) => s.type === "bag" && s.is_default) ||
          (sensitivities || []).find((s) => s.type === "bag" && s.level === 1);
        if (defaultBody) newData.body_sensitivity = defaultBody.id;
        if (defaultBag) newData.bag_sensitivity = defaultBag.id;
      }
      this.setState({ data: newData, errors: errors || {} });
    });
  }

  componentWillUnmount() {
    this.removeCheckboxesEventListener();
  }

  delete = async () => {
    let status = "deleting";
    this.setState({ status });
    try {
      await deleteStore(this.state.data.id);
      status = "deleted";
      this.setState({ status });
    } catch (exception) {
      if (exception.response) {
        toast.error("Failed to delete!");
      } else {
        console.log(exception);
        toast.error("Failed to delete!");
      }
      this.setState({ status });
    }
  };

  save = async () => {
    if (this.state.isSaving) return;

    // Check gesture validation if relevance check is active
    if (this.state.data.relevance_check === "active") {
      const relevanceCheckGesture = this.state.data.client_relevance_check_gestures || {};
      const hasAtLeastOneGesture = Object.values(relevanceCheckGesture).some((value) => value === true);

      if (!hasAtLeastOneGesture) {
        this.setState({
          errors: {
            ...this.state.errors,
            gestureValidation: "At least one gesture must be enabled",
          },
        });
        toast.error("Please enable at least one gesture before saving.");
        return;
      }
    }

    this.setState({ isSaving: true });
    try {
      const { data: store } = await saveStore(this.state.data);

      const desiredIsKeyAccount = Boolean(this.state.data.is_key_account);
      const keyAccountChanged = desiredIsKeyAccount !== this.initialIsKeyAccount;
      if (keyAccountChanged && store?.id) {
        try {
          await toggleStoreKeyAccount(store.id, desiredIsKeyAccount);
          this.initialIsKeyAccount = desiredIsKeyAccount;
        } catch {
          toast.error("Store saved but Key Account flag could not be updated.");
        }
      }

      if (this.props.newStore) {
        const redirectStore = store.id;
        this.setState({ redirectStore });
      } else {
        this.props.fetchStore(store.id);
      }
      toast.info("Store config updated.");
    } catch (ex) {
      if (ex.response) {
        if (ex.response.status === 400) {
          console.log(ex.response);
          Object.keys(ex.response.data).forEach(function (key) {
            const raw = ex.response.data[key];
            const value = Array.isArray(raw) ? raw[0] : typeof raw === "string" ? raw : JSON.stringify(raw);
            if (key === "use_sensitivity") {
              toast.error("Cannot enable Use Sensitivity: Simone version must be >= 1.28.");
            } else {
              toast.error(`${key}: ${value}`);
            }
          });
        } else if (ex.response.status === 404) {
          toast.error("Store not found");
        } else {
          toast.error("Failed to update");
        }
      } else {
        toast.error("Failed to update");
      }
    } finally {
      setTimeout(() => {
        this.setState({ isSaving: false });
      }, 1000);
    }
  };

  restartInference = async () => {
    this.props.toggleRestartMode();
    this.setState({ isRestarting: true });
    try {
      const hostnames = await getStoreHostnames(this.state.data.location);
      await restartWithToast(hostnames, "simone-inference");
    } catch (ex) {
      toast.error(ex.response?.status === 404 ? "Store not found" : "Failed to list the store servers");
    } finally {
      this.setState({ isRestarting: false });
    }
  };

  sleep = (ms) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  toggleModal = () => {
    this.setState({ showModal: !this.state.showModal });
  };

  setUploadBucketBasedOnInference(data) {
    const { uploadBuckets } = this.state;
    const { newStore } = this.props;

    // Only apply this logic for new stores
    if (!newStore) {
      return data;
    }

    if (!uploadBuckets || uploadBuckets.length === 0) {
      return data;
    }

    // Find the bucket with name "veesion-alerts-global-eu-central-1"
    const cloudBucket = uploadBuckets.find((bucket) => bucket.name === STORE_DEFAULTS.CLOUD_INFERENCE.UPLOAD_BUCKET);

    if (data.local_inference) {
      // Local inference: use default value (1)
      data.upload_bucket = STORE_DEFAULTS.UPLOAD_BUCKET_IDX;
    } else {
      // Cloud inference: use the specific bucket
      data.upload_bucket = cloudBucket ? cloudBucket.id : STORE_DEFAULTS.UPLOAD_BUCKET_IDX;
    }

    return data;
  }

  handleLocalInferenceClicked = (checked) => {
    const { newStore } = this.props;

    if (checked) {
      if (newStore) {
        const newData = this.setUploadBucketBasedOnInference({
          ...this.state.data,
          local_inference: true,
        });
        this.setState({ data: newData });
      } else {
        this.setState({ data: { ...this.state.data, local_inference: true } });
      }
    } else {
      this.setState({ showLocalInferenceModal: true });
    }
  };

  confirmDisableLocalInference = () => {
    const { newStore } = this.props;

    if (newStore) {
      const newData = this.setUploadBucketBasedOnInference({
        ...this.state.data,
        local_inference: false,
      });
      this.setState({
        data: newData,
        showLocalInferenceModal: false,
      });
    } else {
      this.setState({
        data: { ...this.state.data, local_inference: false },
        showLocalInferenceModal: false,
      });
    }
  };

  cancelDisableLocalInference = () => {
    this.setState({
      showLocalInferenceModal: false,
    });
  };

  handleInProductionChange = (checked) => {
    const newData = { ...this.state.data, in_production: checked };

    // Set alert_channel to null when in_production is false
    if (!checked) {
      newData.alert_channel = null;
    }

    this.setState({ data: newData });
  };

  handleBboxOverlayChange = ({ currentTarget: input }) => {
    const { data } = this.state;
    const val = input.value;
    const enableBboxClientOverlay = val === "true" ? true : val === "false" ? false : null;
    this.setState({ data: { ...data, enable_bbox_client_overlay: enableBboxClientOverlay }, status: "edited" });
  };

  renderBboxOverlayField() {
    const { data } = this.state;
    const disabled = data.bbox_eligibility && !data.bbox_eligibility.can_activate;
    const reason = disabled ? data.bbox_eligibility.reason : null;
    const value = data.enable_bbox_client_overlay === null ? "default" : String(data.enable_bbox_client_overlay);
    const options = [
      { id: "default", name: "Default (use global config)" },
      { id: "true", name: "Enabled" },
      { id: "false", name: "Disabled" },
    ];
    const wrapperId = `bbox-overlay-wrapper-${data?.id ?? "new"}`;

    return (
      <div id={wrapperId}>
        {this.renderSelect("enable_bbox_client_overlay", "Bounding box client overlay", options, {
          value,
          disabled,
          onChange: this.handleBboxOverlayChange,
          style: disabled ? { pointerEvents: "none" } : undefined,
        })}
        {disabled && reason && (
          <UncontrolledTooltip placement="top" target={wrapperId}>
            {reason}
          </UncontrolledTooltip>
        )}
      </div>
    );
  }

  handleRelevantCheckGestureChange = (gestureKey, checked) => {
    const { data } = this.state;
    const currentRelevantCheckGesture = data.client_relevance_check_gestures || {};

    // Create a new object to maintain immutability
    const newRelevantCheckGesture = {
      ...currentRelevantCheckGesture,
      [gestureKey]: checked,
    };

    // Only validate gestures when relevance_check is "active"
    const newErrors = { ...this.state.errors };
    if (data.relevance_check === "active") {
      const hasAtLeastOneGesture = Object.values(newRelevantCheckGesture).some((value) => value === true);

      if (hasAtLeastOneGesture) {
        delete newErrors.gestureValidation;
      } else {
        newErrors.gestureValidation = "At least one gesture must be enabled";
      }
    } else {
      // Remove gesture validation error when relevance_check is not "active"
      delete newErrors.gestureValidation;
    }

    this.setState({
      data: {
        ...data,
        client_relevance_check_gestures: newRelevantCheckGesture,
      },
      errors: newErrors,
    });
  };

  handleGesturePresetChange = (value) => {
    const { gesturePresets, data } = this.state;
    const preset = gesturePresets[value];

    if (!preset) {
      return;
    }

    // Update the gesture_preset field
    let newData = { ...data, gesture_preset: value };

    // If the preset has gestures (not custom), apply them
    if (preset.gestures) {
      Object.assign(newData, preset.gestures);
    }

    // Update parent toggles based on children states
    newData = this.updateParentGroupsFromData(newData);

    this.setState({ data: newData });
  };

  formatActivationDate = (rawDate) => {
    if (!rawDate) return "";
    const parsedDate = new Date(rawDate);
    if (Number.isNaN(parsedDate.getTime())) {
      return rawDate;
    }
    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");
    return `${year}/${month}/${day}`;
  };

  handleSubmit = (e) => {
    e.preventDefault();

    // Check gesture validation if relevance check is active
    if (this.state.data.relevance_check === "active") {
      const relevanceCheckGesture = this.state.data.client_relevance_check_gestures || {};
      const hasAtLeastOneGesture = Object.values(relevanceCheckGesture).some((value) => value === true);

      if (!hasAtLeastOneGesture) {
        this.setState({
          errors: {
            ...this.state.errors,
            gestureValidation: "At least one gesture must be enabled",
          },
        });
        return;
      }
    }

    const errors = this.validate();
    this.setState({ errors: errors || {} });

    if (errors) {
      console.log(errors);
      return;
    }
    this.doSubmit();
  };

  render() {
    const {
      data,
      gesturePresets,
      toggleConfigsOldLabel,
      toggleConfigBodyParent,
      toggleConfigsNewLabelBody,
      toggleConfigBagParent,
      toggleConfigsNewLabelBag,
      toggleConfigOthersusParent,
      toggleConfigsNewLabelOtherSus,
      redirectStore,
      slackChannels,
      storeManagers,
      buttonsTypes,
      status,
      showLocalInferenceModal,
      isSaving,
      errors,
    } = this.state;

    const { newStore, deleteMode, restartMode } = this.props;
    const bodySensitivityOptions = (this.state.sensitivities || []).filter((s) => s.type === "body");
    const bagSensitivityOptions = (this.state.sensitivities || []).filter((s) => s.type === "bag");

    const defaultBodySensitivity = bodySensitivityOptions.find((s) => s.is_default);
    const defaultBagSensitivity = bagSensitivityOptions.find((s) => s.is_default);
    const latencySensitivityOptions = [
      { id: "normal", name: "Normal" },
      { id: "high", name: "High" },
    ];

    const isInOnboarding = data?.in_onboarding_status === "enabled";
    const remainingDaysRaw = data?.in_onboarding_remaining_days;
    const numericRemainingDays =
      remainingDaysRaw === "" || remainingDaysRaw === null || remainingDaysRaw === undefined
        ? null
        : Number(remainingDaysRaw);
    const isPresetLocked = data.gesture_preset && data.gesture_preset !== "custom";
    const latencyMapping = data.global_config?.latency_sensitivity_mapping;

    if (redirectStore) {
      return <Redirect to={`/stores/${redirectStore}`} />;
    }

    if (status === "deleted") {
      return <Redirect to="/stores/" />;
    }

    return (
      <Card>
        <CardHeader>
          <h5 className="card-category">Store config</h5>
        </CardHeader>
        <CardBody>
          <FormDataContext.Provider value={data}>
            <Form onSubmit={this.handleSubmit}>
              {/* Store identity section */}
              <ResponsiveRow
                fields={[
                  new FieldBuilder()
                    .setId("location")
                    .setName("location")
                    .setComponent(
                      this.renderInput("location", "Location", null, {
                        disabled: !newStore,
                      })
                    )
                    .build(),
                  new FieldBuilder()
                    .setId("name")
                    .setName("name")
                    .setComponent(this.renderInput("name", "Store name"))
                    .build(),
                  new FieldBuilder()
                    .setId("store_manager")
                    .setName("store_manager")
                    .setComponent(this.renderSelect("store_manager", "Store manager", storeManagers))
                    .build(),
                  new FieldBuilder()
                    .setId("telegram_group_id")
                    .setName("telegram_group_id")
                    .setComponent(this.renderInput("telegram_group_id", "Telegram Group ID"))
                    .build(),
                ]}
              />

              <ResponsiveRow
                className="flex-row-reverse justify-between"
                fields={[
                  new FieldBuilder()
                    .setId("send_to_app")
                    .setName("send_to_app")
                    .setComponent(this.renderToggleSwitch("send_to_app", "Send To App"))
                    .build(),
                  new FieldBuilder()
                    .setId("uid")
                    .setName("uid")
                    .setComponent(<ReadOnlyField label="Store UID" value={data.uid} />)
                    .build(),
                ]}
              />

              {/* Onboarding presetting section */}
              <ResponsiveRow
                header="Onboarding presetting"
                fields={[
                  new FieldBuilder()
                    .setId("activation_date")
                    .setName("activation_date")
                    .setComponent(
                      <ReadOnlyField
                        label="Activation date"
                        value={data.activation_date}
                        formatter={this.formatActivationDate}
                      />
                    )
                    .build(),
                  new FieldBuilder()
                    .setId("in_onboarding_status")
                    .setName("in_onboarding_status")
                    .setComponent(<ReadOnlyField label="Status" value={isInOnboarding ? "Enabled" : "Disabled"} />)
                    .build(),
                  new FieldBuilder()
                    .setId("in_onboarding_remaining_days")
                    .setName("in_onboarding_remaining_days")
                    .setComponent(
                      <ReadOnlyField
                        label="Remaining days"
                        value={numericRemainingDays !== null && numericRemainingDays > 0 ? numericRemainingDays : "-"}
                      />
                    )
                    .build(),
                ]}
              />

              <KeyAccountSection
                data={data}
                setValue={this.setValueAndNotify}
                canToggle={Boolean(data.can_toggle_key_account)}
                disabled={this.state.isSaving}
              />

              <AlertChannelForm
                data={data}
                setValue={this.setValueAndNotify}
                disabled={
                  !!(
                    this.props.store &&
                    this.props.store.active_status === "inactive" &&
                    !(this.props.store.location || "").startsWith("demo-")
                  )
                }
              />

              <ResponsiveRow
                fields={[
                  new FieldBuilder()
                    .setId("alert_expiration")
                    .setName("alert_expiration")
                    .setComponent(
                      this.renderInput("alert_expiration", "Alert expiration (seconds)", "number", {
                        step: 1,
                        min: process.env.REACT_APP_MIN_ALERT_EXPIRATION,
                        max: process.env.REACT_APP_MAX_ALERT_EXPIRATION,
                      })
                    )
                    .build(),
                  new FieldBuilder()
                    .setId("slack_channel")
                    .setName("slack_channel")
                    .setComponent(this.renderSelect("slack_channel", "Slack Channel", slackChannels))
                    .build(),
                ]}
              />

              <ResponsiveRow
                fields={[
                  new FieldBuilder()
                    .setId("buttons_type")
                    .setName("buttons_type")
                    .setComponent(this.renderSelect("buttons_type", "Buttons Type", buttonsTypes))
                    .build(),
                  new FieldBuilder()
                    .setId("allow_dataset")
                    .setName("allow_dataset")
                    .setParentTagClassName("pl-md-1")
                    .setComponent(this.renderToggleSwitch("allow_dataset", "Allow Dataset"))
                    .build(),
                ]}
              />

              <ResponsiveRow
                fields={[
                  new FieldBuilder()
                    .setId("relevance_check")
                    .setName("relevance_check")
                    .setComponent(
                      this.renderSelect("relevance_check", "Relevance Check", [
                        { id: "inactive", name: "Inactive" },
                        { id: "active", name: "Active" },
                      ])
                    )
                    .build(),
                ]}
              />

              {/* Gesture Sensitivity Section */}
              {data.relevance_check === "active" && (
                <div className="mb-3">
                  <div className="card border-primary">
                    <div className="card-header bg-primary text-white">
                      <h6 className="p-1">
                        <span className="text-sm">
                          <i className="fas fa-bell mr-2" />
                          Relevance Check - Gestures
                        </span>
                      </h6>
                    </div>
                    <div className="card-body">
                      {this.state.errors.gestureValidation && (
                        <div className="alert alert-danger mb-3">
                          <i className="fas fa-exclamation-triangle mr-2" />
                          {this.state.errors.gestureValidation}
                        </div>
                      )}
                      <Row>
                        {this.state.gestureConfigs.map((config) => (
                          <Col md="4" key={config.key}>
                            <ToggleSwitch
                              name={config.key}
                              id={config.key}
                              checked={data.client_relevance_check_gestures?.[config.key] || false}
                              onChange={(e) => this.handleRelevantCheckGestureChange(config.key, e.target.checked)}
                              label={config.label}
                            />
                          </Col>
                        ))}
                      </Row>
                    </div>
                  </div>
                </div>
              )}

              <ResponsiveRow
                fields={[
                  new FieldBuilder()
                    .setId("local_inference")
                    .setName("local_inference")
                    .setParentTagClassName(data.local_inference ? "pr-md-1" : "orange-critical pr-md-1")
                    .setComponent(
                      <ToggleSwitch
                        name="local_inference"
                        id="local_inference"
                        checked={data.local_inference}
                        onChange={(e) => this.handleLocalInferenceClicked(e.target.checked)}
                        label={data.local_inference ? "Local inference" : "Cloud inference"}
                      />
                    )
                    .build(),
                ]}
              />

              <ResponsiveRow
                md="3"
                fields={[
                  new FieldBuilder()
                    .setId("use_sensitivity")
                    .setName("use_sensitivity")
                    .setComponent(
                      this.renderToggleSwitch(
                        "use_sensitivity",
                        "Use Sensitivity",
                        false,
                        !!this.state.data.use_sensitivity
                      )
                    )
                    .build(),
                  new FieldBuilder()
                    .setId("body_sensitivity")
                    .setName("body_sensitivity")
                    .setComponent(
                      this.renderSelect("body_sensitivity", "Body Sensitivity", bodySensitivityOptions, {
                        disabled: !this.state.data.use_sensitivity,
                        ...(!this.state.data.use_sensitivity && defaultBodySensitivity
                          ? { value: defaultBodySensitivity.id }
                          : {}),
                      })
                    )
                    .build(),
                  new FieldBuilder()
                    .setId("bag_sensitivity")
                    .setName("bag_sensitivity")
                    .setComponent(
                      this.renderSelect("bag_sensitivity", "Bag Sensitivity", bagSensitivityOptions, {
                        disabled: !this.state.data.use_sensitivity,
                        ...(!this.state.data.use_sensitivity && defaultBagSensitivity
                          ? { value: defaultBagSensitivity.id }
                          : {}),
                      })
                    )
                    .build(),
                  new FieldBuilder()
                    .setId("latency_sensitivity")
                    .setName("latency_sensitivity")
                    .setComponent(
                      <div>
                        {this.renderSelect("latency_sensitivity", "Latency Sensitivity", latencySensitivityOptions)}
                        {latencyMapping && (
                          <small className="text-muted d-block mt-1">
                            Alert expiration: {latencyMapping[data.latency_sensitivity] ?? "—"}s (Normal ={" "}
                            {latencyMapping.normal}s, High = {latencyMapping.high}s)
                          </small>
                        )}
                      </div>
                    )
                    .build(),
                ]}
              />

              <ResponsiveRow
                md="4"
                fields={[
                  new FieldBuilder()
                    .setId("enable_bbox_client_overlay")
                    .setName("enable_bbox_client_overlay")
                    .setComponent(this.renderBboxOverlayField())
                    .build(),
                ]}
              />

              <ResponsiveRow
                md="4"
                bottomSeparator={false}
                fields={[
                  new FieldBuilder()
                    .setId("gesture_preset")
                    .setName("gesture_preset")
                    .setComponent(
                      this.renderSelect(
                        "gesture_preset",
                        "Gesture Preset",
                        Object.entries(gesturePresets).map(([id, preset]) => ({ id, name: preset.name })),
                        { onChange: (e) => this.handleGesturePresetChange(e.target.value) }
                      )
                    )
                    .build(),
                ]}
              />

              <div style={{ display: showOldLabels ? "block" : "none" }}>
                <h5 className="card-category">{showNewLabels ? "Old labels" : "Labels"}</h5>
                <ResponsiveRow
                  md="4"
                  fields={toggleConfigsOldLabel.map((config) =>
                    new FieldBuilder()
                      .setId(config.name)
                      .setName(config.name)
                      .setComponent(
                        this.renderToggleSwitch(
                          config.name,
                          config.value,
                          config.inverted,
                          config.disabled,
                          config.datagroup
                        )
                      )
                      .build()
                  )}
                />
              </div>

              {showNewLabels && (
                <div>
                  <h5 className="card-category">Labels</h5>
                  <div
                    className={isPresetLocked ? "gesture-preset-locked" : ""}
                    title={
                      isPresetLocked
                        ? "Individual gestures can only be edited in Custom mode. Warning: Switching to Custom will remove the client's chosen preset."
                        : ""
                    }
                    onClickCapture={isPresetLocked ? (e) => e.stopPropagation() : undefined}
                    onMouseDownCapture={isPresetLocked ? (e) => e.preventDefault() : undefined}
                  >
                    <div>
                      <ResponsiveRow
                        md="4"
                        bottomSeparator={false}
                        fields={[
                          new FieldBuilder()
                            .setId(toggleConfigBodyParent.name)
                            .setName(toggleConfigBodyParent.name)
                            .setParentTagClassName(data.bodyToggleGroup ? "green pr-md-1" : "pr-md-1")
                            .setComponent(
                              this.renderToggleSwitch(
                                toggleConfigBodyParent.name,
                                toggleConfigBodyParent.value,
                                toggleConfigBodyParent.inverted,
                                toggleConfigBodyParent.disabled,
                                toggleConfigBodyParent.datagroup,
                                toggleConfigBodyParent.parentgroup,
                                toggleConfigBodyParent.childgroup
                              )
                            )
                            .build(),
                        ]}
                      />
                      <ResponsiveRow
                        md="4"
                        fields={toggleConfigsNewLabelBody.map((config) =>
                          new FieldBuilder()
                            .setId(config.name)
                            .setName(config.name)
                            .setComponent(
                              this.renderToggleSwitch(
                                config.name,
                                config.value,
                                config.inverted,
                                config.disabled,
                                config.datagroup,
                                config.parentgroup,
                                config.childgroup,
                                config.tooltip
                              )
                            )
                            .build()
                        )}
                      />
                      <ResponsiveRow
                        md="4"
                        bottomSeparator={false}
                        fields={[
                          new FieldBuilder()
                            .setId(toggleConfigBagParent.name)
                            .setName(toggleConfigBagParent.name)
                            .setParentTagClassName(data.bagToggleGroup ? "green pr-md-1" : "pr-md-1")
                            .setComponent(
                              this.renderToggleSwitch(
                                toggleConfigBagParent.name,
                                toggleConfigBagParent.value,
                                toggleConfigBagParent.inverted,
                                toggleConfigBagParent.disabled,
                                toggleConfigBagParent.datagroup,
                                toggleConfigBagParent.parentgroup,
                                toggleConfigBagParent.childgroup
                              )
                            )
                            .build(),
                        ]}
                      />
                      <ResponsiveRow
                        md="4"
                        fields={toggleConfigsNewLabelBag.map((config) =>
                          new FieldBuilder()
                            .setId(config.name)
                            .setName(config.name)
                            .setComponent(
                              this.renderToggleSwitch(
                                config.name,
                                config.value,
                                config.inverted,
                                config.disabled,
                                config.datagroup,
                                config.parentgroup,
                                config.childgroup,
                                config.tooltip
                              )
                            )
                            .build()
                        )}
                      />
                      <ResponsiveRow
                        md="4"
                        bottomSeparator={false}
                        fields={[
                          new FieldBuilder()
                            .setId(toggleConfigOthersusParent.name)
                            .setName(toggleConfigOthersusParent.name)
                            .setParentTagClassName(data.othersusToggleGroup ? "green pr-md-1" : "pr-md-1")
                            .setComponent(
                              this.renderToggleSwitch(
                                toggleConfigOthersusParent.name,
                                toggleConfigOthersusParent.value,
                                toggleConfigOthersusParent.inverted,
                                toggleConfigOthersusParent.disabled,
                                toggleConfigOthersusParent.datagroup,
                                toggleConfigOthersusParent.parentgroup,
                                toggleConfigOthersusParent.childgroup
                              )
                            )
                            .build(),
                        ]}
                      />
                      <ResponsiveRow
                        md="4"
                        fields={toggleConfigsNewLabelOtherSus.map((config) =>
                          new FieldBuilder()
                            .setId(config.name)
                            .setName(config.name)
                            .setComponent(
                              this.renderToggleSwitch(
                                config.name,
                                config.value,
                                config.inverted,
                                config.disabled,
                                config.datagroup,
                                config.parentgroup,
                                config.childgroup,
                                config.tooltip
                              )
                            )
                            .build()
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}
              {/* Store metadata — read-only fields from manager-api */}
              <ResponsiveRow
                header="Store metadata"
                fields={[
                  new FieldBuilder()
                    .setId("category")
                    .setName("category")
                    .setComponent(<ReadOnlyField label="Category" value={data.category} />)
                    .build(),
                  new FieldBuilder()
                    .setId("zendesk_id")
                    .setName("zendesk_id")
                    .setComponent(<ReadOnlyField label="Zendesk ID" value={data.zendesk_id} />)
                    .build(),
                ]}
              />

              <Modal
                isOpen={showLocalInferenceModal}
                toggle={this.cancelDisableLocalInference}
                contentClassName="bg-modal"
              >
                <ModalHeader>Confirm Disable Local Inference</ModalHeader>
                <ModalBody>Are you sure you want to disable local inference?</ModalBody>
                <ModalFooter>
                  <Button className="mr-2" color="danger" onClick={this.confirmDisableLocalInference}>
                    Yes, disable
                  </Button>
                  <Button color="secondary" onClick={this.cancelDisableLocalInference}>
                    Cancel
                  </Button>
                </ModalFooter>
              </Modal>

              <Row>
                {!newStore && !restartMode && (
                  <Col>
                    <Button
                      className="float-left btn-primary m-2"
                      disabled={this.state.isRestarting}
                      onClick={this.props.toggleRestartMode}
                    >
                      <i className="fas fa-power-off mr-2" /> Restart Inference
                    </Button>
                  </Col>
                )}
                {restartMode && (
                  <Col className="restart-confirmation">
                    <span className="float-left">Are you sure?</span>
                    <Button className="float-left btn-primary m-2" onClick={this.restartInference}>
                      <i className="fas fa-power-off mr-2" /> Restart Inference
                    </Button>

                    <Button className="float-left m-2" onClick={this.props.toggleRestartMode}>
                      <i className="fas fa-times mr-2" />
                      Cancel
                    </Button>
                  </Col>
                )}
                {!deleteMode && (
                  <Col>
                    <Button
                      disabled={Object.keys(errors).length !== 0 || isSaving}
                      className="float-right btn-success m-2"
                      onClick={this.save}
                    >
                      <i className="fas fa-save mr-2" />
                      {isSaving ? "Saving..." : "Save"}
                    </Button>
                    <DisplayWhen condition={userHasRole(editorRole) && !userHasRole(qualityRole)}>
                      {!newStore && (
                        <Button className="float-right btn-danger m-2" onClick={this.props.toggleDeleteMode}>
                          <i className="fas fa-trash-alt mr-2" /> Delete
                        </Button>
                      )}
                    </DisplayWhen>
                  </Col>
                )}
                {deleteMode && (
                  <Col className="delete-confirmation">
                    <Button className="float-right btn-danger m-2" onClick={this.delete}>
                      <i className="fas fa-trash-alt mr-2" /> Delete
                    </Button>

                    <Button className="float-right m-2" onClick={this.props.toggleDeleteMode}>
                      <i className="fas fa-times mr-2" />
                      Cancel
                    </Button>
                    <span className="float-right">Are you sure?</span>
                  </Col>
                )}
              </Row>
            </Form>
          </FormDataContext.Provider>
        </CardBody>
      </Card>
    );
  }
}

export default withRouter(StoreConfigForm);
